import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getAmbulanceRequest, updateAmbulanceStatus } from '../../api/ambulance.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'
import CrossFacilityErrorCard from '../../components/CrossFacilityErrorCard.jsx'
import { formatDateTime, statusColor } from '../../utils/format.js'
import { AMBULANCE_TRANSITIONS } from '../../constants/index.js'

/**
 * Dispatcher view for a single ambulance request. Shows pickup/destination,
 * urgency, and the clinical summary written by the ordering clinician.
 * Actions drive the status machine: accept -> dispatch -> complete, or
 * reject at the start.
 */
export default function AmbulanceRequestDetail() {
  const { requestId } = useParams()
  const qc = useQueryClient()
  const [summary, setSummary] = useState('')

  const q = useQuery({
    queryKey: ['ambulance-request', requestId],
    queryFn: () => getAmbulanceRequest(requestId),
    enabled: Boolean(requestId),
  })
  const r = q.data

  const mut = useMutation({
    mutationFn: ({ next, summary }) => {
      // The dispatcher note must APPEND to the existing clinical
      // summary, not replace it — overwriting wipes the ordering
      // clinician's (or patient's) original presenting complaint
      // and breaks the audit narrative. Helper text under the input
      // promises append behaviour; do that here on the way to the
      // PATCH. Each state transition's note is stamped with the
      // status verb so the trail reads like a log.
      const existing = (r?.clinical_summary || '').trim()
      const note = (summary || '').trim()
      let combined = existing
      if (note) {
        const stamp = `[${(next || '').toUpperCase()}] `
        combined = existing
          ? `${existing}\n${stamp}${note}`
          : `${stamp}${note}`
      }
      return updateAmbulanceStatus(requestId, next, combined || undefined)
    },
    onSuccess: (fresh) => {
      qc.setQueryData(['ambulance-request', requestId], fresh)
      qc.invalidateQueries({ queryKey: ['ambulance-queue'] })
      setSummary('')
    },
  })

  if (q.isLoading) return <div className="p-4 text-sm text-slate-400">Loading…</div>
  if (q.isError || !r) {
    // Cross-service 403 → CrossFacilityErrorCard renders the dedicated
    // tel: call screen. Returns null on any other error so we fall
    // through to the generic ErrorBanner below.
    const crossCard = (
      <CrossFacilityErrorCard
        error={q.error}
        backTo="/ambulance"
        backLabel="Back to queue"
      />
    )
    const env = q.error?.response?.data?.error
    if (env?.code === 'cross_service_access') {
      return <div className="space-y-3">{crossCard}</div>
    }
    return (
      <div className="space-y-3">
        <ErrorBanner error={q.error || new Error('Request not found')} onRetry={() => q.refetch()} />
        <Link to="/ambulance" className="text-xs underline">Back to queue</Link>
      </div>
    )
  }

  const nexts = AMBULANCE_TRANSITIONS[(r.status || '').toLowerCase()] || []

  // Patient-banner identity helpers — same pattern as the lab + radiology
  // detail pages. The crew confirms the recipient before dispatching.
  const ageYears = (() => {
    if (!r.patient_dob) return null
    const d = new Date(r.patient_dob)
    if (isNaN(d.getTime())) return null
    const now = new Date()
    let a = now.getFullYear() - d.getFullYear()
    const m = now.getMonth() - d.getMonth()
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) a -= 1
    return a
  })()

  return (
    <div className="space-y-4">
      {/* Patient banner — prominent so the dispatcher / crew confirm the
          recipient before any vehicle leaves. */}
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-slate-400">Patient</div>
            <div className="text-xl font-semibold text-slate-800 break-words">
              {r.patient_name || 'Unknown patient'}
            </div>
            <div className="mt-1 text-xs text-slate-500 flex flex-wrap gap-x-3 gap-y-1">
              {r.patient_sex && <span className="capitalize">{r.patient_sex}</span>}
              {r.patient_dob && (
                <span>
                  DOB {r.patient_dob}
                  {ageYears !== null && ` · ${ageYears} y`}
                </span>
              )}
              {r.patient_phone && <span>☎ {r.patient_phone}</span>}
            </div>
          </div>
          <Link to="/ambulance" className="text-xs underline">Back</Link>
        </div>
      </Card>

      <Card>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-400">Ambulance request</div>
          <div className="font-semibold">#{r.id} — {r.service_type}</div>
          <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
            <span className={`uppercase border rounded-full px-2 py-0.5 text-[10px] ${statusColor(r.status)}`}>{r.status}</span>
            <span className="uppercase border border-slate-200 rounded-full px-2 py-0.5 text-[10px] text-slate-600">{r.urgency}</span>
            {r.initiated_by === 'patient' && (
              <span
                className="uppercase border rounded-full px-2 py-0.5 text-[10px] bg-amber-50 border-amber-300 text-amber-800"
                title="Patient submitted this directly via the app"
              >
                via patient
              </span>
            )}
            <span>Requested {formatDateTime(r.created_at)}</span>
            {r.ambulance_service_name && <span>· {r.ambulance_service_name}</span>}
          </div>
        </div>
        {r.initiated_by === 'patient' && (
          <div className="mt-3 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            <b>Triage call recommended.</b> This request came from the
            patient app without a doctor pre-screen. Confirm urgency and
            clinical fit by phone before dispatching the crew.
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Route" />
        <div className="text-sm">
          <div><span className="uppercase tracking-wide text-[10px] text-slate-400">Pickup</span>
            <div>{r.pickup_location || '—'}</div>
          </div>
          <div className="mt-2"><span className="uppercase tracking-wide text-[10px] text-slate-400">Destination</span>
            <div>{r.destination || '—'}</div>
          </div>
        </div>
        {r.ambulance_service_name && (
          <div className="mt-3 text-xs text-slate-500">
            Assigned to {r.ambulance_service_name}
          </div>
        )}
      </Card>

      {r.clinical_summary && (
        <Card>
          <CardHeader title="Clinical summary" />
          <div className="text-sm whitespace-pre-wrap">{r.clinical_summary}</div>
        </Card>
      )}

      {nexts.length > 0 && (
        <Card>
          <CardHeader title="Dispatcher actions" subtitle="Update status visible to the ordering clinician and patient" />
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            placeholder="Optional dispatcher note (appended to clinical summary)"
            className="w-full border rounded px-2 py-1.5 text-sm"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            {nexts.map(({ label, next }) => (
              <button
                key={next}
                onClick={() => mut.mutate({ next, summary })}
                disabled={mut.isPending}
                className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
              >
                {label}
              </button>
            ))}
          </div>
          {mut.isError && <div className="mt-2"><ErrorBanner error={mut.error} /></div>}
        </Card>
      )}
    </div>
  )
}
